import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  directionLabel,
  directionTone,
  enabledLabel,
  enabledTone,
  formatNumberValue,
  formatPercentValue,
  FUTURES_GLOBAL_LINK,
} from './controlConstants';
import { getControlSchema } from './controlSchema';
import {
  buildPersonalPageParams,
  buildPersonalSavePayload,
  fieldLabel,
  personalSupportsMinMax,
  personalToForm,
} from './controlFormUtils';
import { OrderControlGlobalModal, OrderControlPersonalModal } from './OrderControlModals';

const EMPTY_FILTERS = {
  enabled: '',
  memberId: '',
};

function GlobalDetailItem({ label, value, tone }) {
  return (
    <div className="config-detail-grid__item">
      <span className="config-detail-grid__label">{label}：</span>
      <span className={`config-detail-grid__value ${tone ? `tone-${tone}` : ''}`}>{value}</span>
    </div>
  );
}

function renderGlobalDetails(playType, record) {
  if (!record) {
    return null;
  }

  if (playType === 'FUTURES') {
    const randomOdds = record.isRealOdds === false;
    return (
      <>
        <GlobalDetailItem
          label={fieldLabel('enabled')}
          value={enabledLabel(record.enabled)}
          tone={enabledTone(record.enabled)}
        />
        <GlobalDetailItem
          label={fieldLabel('isRealOdds')}
          value={randomOdds ? '隨機賠率' : '真實賠率'}
        />
        {randomOdds && (
          <>
            <GlobalDetailItem
              label={fieldLabel('minRandomOdds')}
              value={formatPercentValue(record.minRandomOdds)}
            />
            <GlobalDetailItem
              label={fieldLabel('maxRandomOdds')}
              value={formatPercentValue(record.maxRandomOdds)}
            />
          </>
        )}
        <GlobalDetailItem
          label={fieldLabel('startKillAmount')}
          value={`${formatNumberValue(record.startKillAmount)} USDT`}
        />
        <GlobalDetailItem
          label={fieldLabel('killProbability')}
          value={`${formatNumberValue(record.killProbability)}%`}
        />
      </>
    );
  }

  return (
    <>
      <GlobalDetailItem
        label={fieldLabel('enabled')}
        value={enabledLabel(record.enabled)}
        tone={enabledTone(record.enabled)}
      />
      <GlobalDetailItem
        label={fieldLabel('direction')}
        value={directionLabel(playType, record)}
        tone={directionTone(playType, record)}
      />
      <GlobalDetailItem
        label={fieldLabel('minValue')}
        value={playType === 'TIME_LIMITED'
          ? formatPercentValue(record.minValue)
          : formatNumberValue(record.minValue)}
      />
      <GlobalDetailItem
        label={fieldLabel('maxValue')}
        value={playType === 'TIME_LIMITED'
          ? formatPercentValue(record.maxValue)
          : formatNumberValue(record.maxValue)}
      />
    </>
  );
}

export function OrderControlPersonalPanel({ playType }) {
  const schema = getControlSchema(playType, 'personal');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling] = useState(false);

  const loadRecords = useCallback(async (pageNo = 1, nextFilters = filters) => {
    if (!playType) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.orderControlPersonalPage(
        buildPersonalPageParams(pageNo, playType, nextFilters)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filters, playType]);

  useEffect(() => {
    loadRecords(1, EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }, [playType, loadRecords]);

  function openCreateModal() {
    setActiveRecord(null);
    setModalOpen(true);
  }

  function openEditModal(record) {
    setActiveRecord(record);
    setModalOpen(true);
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setActionMsg('');
    try {
      if (payload.id) {
        await adminApi.updateOrderControlPersonal(payload);
      } else {
        await adminApi.saveOrderControlPersonal(payload);
      }
      setActionMsg('保存成功');
      setModalOpen(false);
      loadRecords(page, filters);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    setActionMsg('');
    try {
      await adminApi.deleteOrderControlPersonal(deleteTarget.id);
      setActionMsg('刪除成功');
      setDeleteTarget(null);
      loadRecords(page, filters);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  async function confirmToggle() {
    if (!toggleTarget) {
      return;
    }
    setToggling(true);
    setActionMsg('');
    try {
      const form = personalToForm(toggleTarget, playType);
      form.enabled = !toggleTarget.enabled;
      const payload = buildPersonalSavePayload(form, playType, toggleTarget);
      await adminApi.updateOrderControlPersonal(payload);
      setActionMsg(toggleTarget.enabled ? '已禁用' : '已啟用');
      setToggleTarget(null);
      loadRecords(page, filters);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '操作失敗');
    } finally {
      setToggling(false);
    }
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1, filters);
  }

  function handleReset() {
    setFilters(EMPTY_FILTERS);
    loadRecords(1, EMPTY_FILTERS);
  }

  return (
    <div className="order-control-panel">
      {schema?.hints?.map((hint) => (
        <p key={hint} className="form-hint form-hint--warning">{hint}</p>
      ))}

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="controlFilterEnabled">狀態</label>
          <select
            id="controlFilterEnabled"
            value={filters.enabled}
            onChange={(event) => setFilters((prev) => ({ ...prev, enabled: event.target.value }))}
          >
            <option value="">全部</option>
            <option value="true">啟用</option>
            <option value="false">禁用</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="controlFilterMemberId">用戶 ID</label>
          <input
            id="controlFilterMemberId"
            type="text"
            inputMode="numeric"
            value={filters.memberId}
            onChange={(event) => setFilters((prev) => ({ ...prev, memberId: event.target.value }))}
            placeholder="請輸入用戶 ID"
          />
        </div>
        <button type="submit" className="btn btn-primary">搜索</button>
        <button type="button" className="btn" onClick={handleReset}>重置</button>
        <button type="button" className="btn btn-primary" onClick={openCreateModal}>新增</button>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {actionMsg && (
        <div className={`alert ${actionMsg.includes('失敗') ? 'alert-error' : 'alert-success'}`}>
          {actionMsg}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>用戶 ID</th>
            <th>控獎方向</th>
            {personalSupportsMinMax(playType) && (
              <>
                <th>{playType === 'TIME_LIMITED' ? '偏移最小值' : '滑價隨機最低值'}</th>
                <th>{playType === 'TIME_LIMITED' ? '偏移最大值' : '滑價隨機最高值'}</th>
              </>
            )}
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {!loading && records.length === 0 ? (
            <tr>
              <td colSpan={personalSupportsMinMax(playType) ? 6 : 4} className="empty-cell">尚無資料</td>
            </tr>
          ) : (
            records.map((row) => (
              <tr key={row.id}>
                <td>{row.memberId ?? '—'}</td>
                <td>
                  <span className={`direction-tag direction-tag--${directionTone(playType, row)}`}>
                    {directionLabel(playType, row)}
                  </span>
                </td>
                {personalSupportsMinMax(playType) && (
                  <>
                    <td>
                      {playType === 'TIME_LIMITED'
                        ? formatPercentValue(row.minValue)
                        : formatNumberValue(row.minValue)}
                    </td>
                    <td>
                      {playType === 'TIME_LIMITED'
                        ? formatPercentValue(row.maxValue)
                        : formatNumberValue(row.maxValue)}
                    </td>
                  </>
                )}
                <td>
                  <span className={`status-pill status-pill--${enabledTone(row.enabled)}`}>
                    {enabledLabel(row.enabled)}
                  </span>
                </td>
                <td className="action-cell">
                  <button type="button" className="btn btn-sm btn-success" onClick={() => openEditModal(row)}>
                    修改
                  </button>
                  <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => setDeleteTarget(row)}>
                    刪除
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setToggleTarget(row)}>
                    {row.enabled ? '禁用' : '啟用'}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="table-footer">
        <span>共 {total} 筆</span>
        <Pagination page={page} totalPages={totalPages} onChange={(nextPage) => loadRecords(nextPage, filters)} />
      </div>

      <OrderControlPersonalModal
        open={modalOpen}
        playType={playType}
        record={activeRecord}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSave}
        submitting={submitting}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除控獎"
        detail={`確定刪除用戶 ${deleteTarget?.memberId ?? ''} 的控獎設定？`}
        confirmLabel="刪除"
        confirming={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(toggleTarget)}
        title={toggleTarget?.enabled ? '禁用控獎' : '啟用控獎'}
        detail={`確定${toggleTarget?.enabled ? '禁用' : '啟用'}用戶 ${toggleTarget?.memberId ?? ''} 的控獎設定？`}
        confirming={toggling}
        onConfirm={confirmToggle}
        onClose={() => setToggleTarget(null)}
      />
    </div>
  );
}

export function OrderControlGlobalPanel({ playType }) {
  const schema = getControlSchema(playType, 'global');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadRecord = useCallback(async () => {
    if (!playType) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.orderControlGlobal(playType);
      setRecord(data || null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '載入失敗');
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [playType]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  async function handleSave(payload) {
    setSubmitting(true);
    setActionMsg('');
    try {
      const saved = await adminApi.saveOrderControlGlobal(payload);
      setRecord(saved || payload);
      setActionMsg('保存成功');
      setModalOpen(false);
      loadRecord();
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="order-control-panel">
      <div className="order-control-global__header">
        <div>
          {schema?.hints?.map((hint) => (
            <p key={hint} className="form-hint form-hint--warning">{hint}</p>
          ))}
          {playType === 'FUTURES' && (
            <p className="form-hint">
              局級場控請至
              {' '}
              <Link to={FUTURES_GLOBAL_LINK.path}>{FUTURES_GLOBAL_LINK.label}</Link>
              {' '}
              操作。
            </p>
          )}
        </div>
        <button type="button" className="btn btn-success" onClick={() => setModalOpen(true)}>
          修改
        </button>
      </div>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {actionMsg && (
        <div className={`alert ${actionMsg.includes('失敗') ? 'alert-error' : 'alert-success'}`}>
          {actionMsg}
        </div>
      )}

      {!loading && (
        <div className="config-detail-grid">
          {record
            ? renderGlobalDetails(playType, record)
            : (
              <>
                <GlobalDetailItem label="狀態" value="尚未配置" />
                <GlobalDetailItem label="說明" value="請點擊「修改」建立全局控獎設定" />
              </>
            )}
        </div>
      )}

      <OrderControlGlobalModal
        open={modalOpen}
        playType={playType}
        record={record}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSave}
        submitting={submitting}
      />
    </div>
  );
}
